import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { Line, LineChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";
import { Button } from "@/components/ui/button";

function setMeta(title, description, canonical) {
  document.title = title;
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
  meta.content = description;
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
  if (canonical) link.href = canonical;
}

const SITE = "https://siyaara.com";

const faqLd = (city, metal) => ({
  "@context": "https://schema.org", "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: `What is today's ${metal} rate in ${city}?`, acceptedAnswer: { "@type": "Answer", text: `Today's ${metal} rate in ${city} is updated live on this page from Siyaara's rate engine.` } },
    { "@type": "Question", name: `Why do ${metal} rates differ across cities?`, acceptedAnswer: { "@type": "Answer", text: "Local demand, logistics and jeweller premiums create small city-level differences over the national base rate." } },
  ],
});

export default function CityRate() {
  const { state, slug } = useParams();
  const metal = window.location.pathname.startsWith("/silver-rate") ? "silver" : "gold";
  const [cities, setCities] = useState([]);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [range, setRange] = useState("30d");

  const citySlug = slug || null;
  const legacySuffix = citySlug ? `-${metal}-rate-today` : null;
  const cleanSlug = citySlug && legacySuffix && citySlug.endsWith(legacySuffix)
    ? citySlug.slice(0, -legacySuffix.length)
    : citySlug;

  useEffect(() => {
    api.get("/rates/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  // Legacy suffixed URLs (…-gold-rate-today) permanently redirect to the clean shape
  const isLegacyUrl = Boolean(citySlug && cleanSlug !== citySlug);

  useEffect(() => {
    if (!cleanSlug) return;
    setData(null); setNotFound(false);
    api.get(`/rates/city/${cleanSlug}`)
      .then((r) => {
        setData(r.data);
        const c = r.data.city;
        const rate = metal === "gold" ? r.data.gold["22K"].per_gram : r.data.silver["925"].per_gram;
        setMeta(
          `${metal === "gold" ? "Gold" : "Silver"} Rate in ${c.name} Today (${fmtDate(new Date())}) — Live ${metal === "gold" ? "22K" : "925"} ${inr(rate)}/g | Siyaara`,
          `Today's ${metal} rate in ${c.name}, ${c.state}: live 9KT, 24K, 22K and 18K ${metal} prices per gram and per 10 grams, 10-day history and trend chart. Updated daily.`,
          `${SITE}/${metal}-rate/${r.data.city.state_slug}/${c.slug}`
        );
      })
      .catch(() => setNotFound(true));
  }, [cleanSlug, metal]);

  useEffect(() => {
    if (data) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "cityrate-jsonld";
      s.text = JSON.stringify(faqLd(data.city.name, metal));
      document.getElementById("cityrate-jsonld")?.remove();
      document.head.appendChild(s);
      return () => document.getElementById("cityrate-jsonld")?.remove();
    }
  }, [data, metal]);

  if (isLegacyUrl) {
    return <Navigate to={`/${metal}-rate/${state}/${cleanSlug}`} replace />;
  }

  // Index page: list all cities
  if (!cleanSlug) {
    const title = metal === "gold" ? "Today's Gold Rate in India — City-wise" : "Today's Silver Rate in India — City-wise";
    setMeta(`${title} | Siyaara`, `Live ${metal} rates across 20+ Indian cities. 9KT, 24K, 22K, 18K per gram prices updated daily.`, `${SITE}/${metal}-rate`);
    const byState = cities.reduce((acc, c) => { (acc[c.state] = acc[c.state] || []).push(c); return acc; }, {});
    return (
      <div className="container-x py-10" data-testid="city-rate-index">
        <p className="eyebrow">LIVE RATES</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">{title}</h1>
        <p className="text-stone-600 mt-2 max-w-2xl">Pick your city for today's rate, 10-day history and price trend.</p>
        {Object.entries(byState).map(([st, list]) => (
          <div key={st} className="mt-8">
            <p className="eyebrow mb-3">{st.toUpperCase()}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {list.map((c) => (
                <Link
                  key={c.slug}
                  to={`/${metal}-rate/${c.state.toLowerCase().replace(/\s+/g, "-")}/${c.slug}`}
                  className="border border-stone-200 bg-white p-4 hover:border-brand transition-colors group"
                  data-testid={`city-link-${c.slug}`}
                >
                  <p className="font-heading font-medium group-hover:text-brand">{c.name}</p>
                  <p className="text-xs text-stone-500 font-mono mt-0.5">{c.state}</p>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (notFound) return <div className="container-x py-20 text-center" data-testid="city-not-found">City not found.</div>;
  if (!data) return <div className="container-x py-20 text-center text-stone-400 font-mono text-sm">LOADING…</div>;

  const c = data.city;
  const isGold = metal === "gold";
  const cards = isGold
    ? [["9KT (Siyaara Stock)", data.gold["9K"] || data.gold["9KT"]], ["24K (Pure)", data.gold["24K"]], ["22K (Standard)", data.gold["22K"]], ["18K (Made to Order)", data.gold["18K"]]].filter((x) => x[1])
    : [["Pure Silver", data.silver.pure], ["925 Sterling", data.silver["925"]]];
  const trendData = data.trend[range] || [];
  const metalLabel = isGold ? "Gold" : "Silver";

  return (
    <div className="container-x py-10" data-testid="city-rate-page">
      <p className="eyebrow flex items-center gap-1"><MapPin size={12} /> {c.state.toUpperCase()}</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1" data-testid="city-rate-h1">
        {metalLabel} Rate in {c.name} Today
      </h1>
      <p className="text-stone-600 mt-3 max-w-3xl leading-relaxed" data-testid="city-intro">
        {(data.intro_template || "").replace(/\bgold\b/gi, metal).replace(/\bGold\b/g, isGold ? "Gold" : "Silver")}{" "}
        Last update: {fmtDate(data.updated_at)}.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
        {cards.map(([label, r]) => (
          <div key={label} className="border border-stone-200 bg-white p-5" data-testid={`rate-card-${label.split(" ")[0].toLowerCase()}`}>
            <p className="text-[11px] font-mono uppercase tracking-widest text-stone-500">{label}</p>
            <p className="font-heading text-2xl font-semibold mt-2">{inr(r.per_gram)}<span className="text-sm font-normal text-stone-400">/g</span></p>
            <p className="text-sm text-stone-500 font-mono mt-1">{inr(r.per_10g)} / 10g</p>
            {r.direction && (
              <p className={`text-xs font-mono mt-2 ${r.direction === "up" ? "text-red-600" : r.direction === "down" ? "text-emerald-700" : "text-stone-400"}`} data-testid={`rate-change-${label.split(" ")[0].toLowerCase()}`}>
                {r.direction === "up" ? "▲" : r.direction === "down" ? "▼" : "—"} {r.change > 0 ? "+" : ""}{inr(Math.abs(r.change))}/g ({r.change_pct > 0 ? "+" : ""}{r.change_pct}%) vs yesterday
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <section className="border border-stone-200 bg-white p-5">
          <h2 className="font-heading font-semibold mb-3">Last 10 days in {c.name}</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-mono uppercase tracking-widest text-stone-500 border-b border-stone-200">
                <th className="py-2">Date</th><th className="text-right">{isGold ? "22K /g" : "925 /g"}</th><th className="text-right pr-1">{isGold ? "24K /g" : "Pure /g"}</th>
              </tr>
            </thead>
            <tbody>
              {[...data.history_10d].reverse().map((d) => (
                <tr key={d.date} className="border-b border-stone-100" data-testid={`rate-row-${d.date}`}>
                  <td className="py-2">{fmtDate(d.date)}</td>
                  <td className="text-right font-mono">{inr(isGold ? d.gold_24k * 0.916 : d.silver * 0.925)}</td>
                  <td className="text-right font-mono pr-1">{inr(isGold ? d.gold_24k : d.silver)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="border border-stone-200 bg-white p-5" data-testid="rate-trend">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-semibold">Price trend</h2>
            <div className="flex gap-1">
              {["30d", "6m", "1y"].map((r) => (
                <button key={r} onClick={() => setRange(r)} className={`px-3 py-1 text-xs font-mono border ${range === r ? "border-stone-900 bg-stone-900 text-white" : "border-stone-200"}`} data-testid={`trend-${r}`}>
                  {r.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} domain={["auto", "auto"]} tickFormatter={(v) => `₹${Math.round(v).toLocaleString("en-IN")}`} />
                <Tooltip formatter={(v) => inr(v)} />
                <Line type="monotone" dataKey={isGold ? "gold_24k" : "silver"} stroke="#A98307" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      <section className="mt-8 border border-stone-200 bg-white p-6">
        <h2 className="font-heading font-semibold mb-4">FAQs — {metalLabel} rate in {c.name}</h2>
        <div className="space-y-4 text-sm">
          <div><p className="font-medium">What is today's {metalLabel.toLowerCase()} rate in {c.name}?</p>
            <p className="text-stone-600 mt-1">As of {fmtDate(data.updated_at)}, {isGold ? `22K gold is ${inr(data.gold["22K"].per_gram)}/g and 24K is ${inr(data.gold["24K"].per_gram)}/g` : `925 silver is ${inr(data.silver["925"].per_gram)}/g and pure silver is ${inr(data.silver.pure.per_gram)}/g`} in {c.name}.</p></div>
          <div><p className="font-medium">Why is the {c.name} rate different from other cities?</p>
            <p className="text-stone-600 mt-1">Local premiums, logistics and demand create small city-level differences over the national base rate. Siyaara shows the exact rate your jewellery price is computed from.</p></div>
          <div><p className="font-medium">Does Siyaara charge making on top of this rate?</p>
            <p className="text-stone-600 mt-1">Yes — making charges, wastage and GST are shown transparently in the price breakup on every product page, computed live from this rate.</p></div>
          <div><p className="font-medium">How often are rates updated?</p>
            <p className="text-stone-600 mt-1">Daily at minimum, and every 15 minutes when the live API source is enabled.</p></div>
        </div>
      </section>

      <div className="mt-8 bg-stone-950 text-white p-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-heading text-2xl font-semibold">Buy at today's rate, locked for 30 minutes</p>
          <p className="text-stone-400 text-sm mt-1">Every Siyaara piece is priced off the live rate you see here.</p>
        </div>
        <Link to={`/shop?metal=${metal}`}>
          <Button className="bg-brand hover:bg-brand-hover text-white btn-press rounded-none px-8" data-testid="city-shop-cta">
            Shop {metalLabel} Jewellery <ArrowRight size={16} className="ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
